import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';
import { useMounted } from '@vueuse/core';

import { SnowplowTitleContextGraphql, SnowplowOldModuleContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { ObjectType } from '@/@types/graphql-types';
import type { TitleType, GlobalModuleName, SponsoredModuleName } from '@/helpers/composables/useGlobalTitles';
import { GetGlobalHomeQuery } from '@/features/global-home/components/graphql/queries/GetGlobalHome.query';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import Top10TitleSwiper from '@/components/popular/provider-module/Top10TitleSwiper.vue';
import GlobalTop10SwiperSlide from '@/features/global-home/components/GlobalTop10SwiperSlide.vue';
import WatchNowButtonNew from '@/components/buttons/WatchNowButtonNew.vue';

import { getThumborUrl } from '@/filters/thumbor';
import { useI18n } from '@/helpers/composables/useI18n';
import { ImpressionTrackingEvents } from '@/enums/events';
import { defaultObservableOptions } from '@/mixins/types/impressionTracking';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getSelectedTab } from '@/components/popular/provider-module/helper';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { storeToRefs } from 'pinia';
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';

type TitlesModuleName = Extract<
	GlobalModuleName,
	| 'MAIN_TOP_10_MOVIES'
	| 'MAIN_TOP_10_SHOWS'
	| 'MAIN_JWTV_TVOD'
	| 'MAIN_JWTV_AVOD'
	| 'MAIN_NEW_MOVIES'
	| 'MAIN_NEW_SHOWS'
	| 'MAIN_TOP_10_GENRE_COMEDY'
	| 'MAIN_TOP_10_GENRE_DRAMA'
	| 'MAIN_TOP_10_GENRE_HORROR'
	| 'MAIN_POPULAR_MOVIES'
	| 'MAIN_POPULAR_SHOWS'
>;

type GlobalTitleEdges = NonNullable<NonNullable<GetGlobalHomeQuery[TitlesModuleName]>['edges']>;

type TitleNode = GlobalTitleEdges[number]['node'];


export default /*#__PURE__*/_defineComponent({
  __name: 'GlobalTitles',
  props: {
    country: null,
    language: null,
    titles: null,
    objectType: null,
    genre: null,
    type: null,
    moduleName: null,
    featured: { type: Boolean },
    isSponsored: { type: Boolean, default: false },
    hideJwScore: { type: Boolean, default: false },
    hideImdbScore: { type: Boolean, default: false }
  },
  emits: ["posterClick", "titleClickout", "watchlistClick", "moreInfoClick", "imageError"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'posterClick', data: { title: TitleNode; index: number }): void;
	(e: 'titleClickout', data: { title: TitleNode; index: number }): void;
	(e: 'watchlistClick', data: { title: TitleNode; index: number }): void;
	(e: 'moreInfoClick', data: { title: TitleNode; index: number }): void;
	(e: 'imageError', src: string | null | undefined): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { country: string, language: string, titles: GlobalTitleEdges, objectType?: ObjectType.Movie | ObjectType.Show, genre?: 'drm' | 'cmy' | 'hrr', type: TitleType, moduleName: GlobalModuleName | SponsoredModuleName, featured?: boolean, isSponsored: boolean, hideJwScore: boolean, hideImdbScore: boolean };





const { t, tc } = useI18n();
const isMounted = useMounted();

const { activeRoute } = storeToRefs(useRoutingStore());
const { isLoggedIn } = storeToRefs(useUserStore());
const { genresByShortName } = storeToRefs(useConstantStore());

// Nearest provider wins - providing here would clobber the SR orchestrator's own placement.
useProvideClickoutPlacement(() => (props.isSponsored ? undefined : 'discovery_top_10_module'));

const posterUrls = computed(() =>
	props.titles.map(edge => getThumborUrl({ url: edge.node.content.posterUrl ?? '', imageType: 'poster' }))
);

const moduleContext = computed(() => new SnowplowOldModuleContext('', -1, props.moduleName, ''));

const genreFullName = computed(() => {
	if (!props.genre) return '';
	return (genresByShortName.value as Record<string, { translation: string }>)[props.genre]?.translation ?? '';
});

const title = computed(() => {
	if (props.moduleName === 'MAIN_JWTV_TVOD') {
		return tc('WEBAPP_GLOBAL_JWTV_TVOD');
	}

	if (props.moduleName === 'MAIN_JWTV_AVOD') {
		return tc('WEBAPP_GLOBAL_FREE_TITLES');
	}

	if (props.genre) {
		return t('WEBAPP_GLOBAL_TOP_10_GENRE', { genre: genreFullName.value });
	}

	if (props.objectType === ObjectType.Movie) {
		return (
			{
				TOP_10: tc('WEBAPP_GLOBAL_TOP_10_MOVIE'),
				UPCOMING: tc('WEBAPP_GLOBAL_UPCOMING_MOVIE'),
				NEW: tc('WEBAPP_GLOBAL_NEW_MOVIE'),
				POPULAR: tc('WEBAPP_GLOBAL_POPULAR_MOVIE'),
			} as Record<TitleType, string>
		)[props.type];
	}

	return (
		{
			TOP_10: tc('WEBAPP_GLOBAL_TOP_10_SHOW'),
			UPCOMING: tc('WEBAPP_GLOBAL_UPCOMING_SHOW'),
			NEW: tc('WEBAPP_GLOBAL_NEW_SHOW'),
			POPULAR: tc('WEBAPP_GLOBAL_POPULAR_SHOW'),
		} as Record<TitleType, string>
	)[props.type];
});

const text = computed(() => {
	if (props.moduleName === 'MAIN_JWTV_TVOD') {
		return tc('WEBAPP_GLOBAL_JWTV_TVOD_TEXT');
	}

	if (props.moduleName === 'MAIN_JWTV_AVOD') {
		return tc('WEBAPP_GLOBAL_FREE_TITLES_TEXT');
	}

	if (props.genre) {
		return t('WEBAPP_GLOBAL_TOP_10_GENRE_TEXT', { genre: genreFullName.value });
	}

	if (props.objectType === ObjectType.Movie) {
		return (
			{
				TOP_10: tc('WEBAPP_GLOBAL_TOP_10_MOVIE_TEXT'),
				UPCOMING: tc('WEBAPP_GLOBAL_UPCOMING_MOVIE_TEXT'),
				NEW: tc('WEBAPP_GLOBAL_NEW_MOVIE_TEXT'),
				POPULAR: tc('WEBAPP_GLOBAL_POPULAR_MOVIE_TEXT'),
			} as Record<TitleType, string>
		)[props.type];
	}

	return (
		{
			TOP_10: tc('WEBAPP_GLOBAL_TOP_10_SHOW_TEXT'),
			UPCOMING: tc('WEBAPP_GLOBAL_UPCOMING_SHOW_TEXT'),
			NEW: tc('WEBAPP_GLOBAL_NEW_SHOW_TEXT'),
			POPULAR: tc('WEBAPP_GLOBAL_POPULAR_SHOW_TEXT'),
		} as Record<TitleType, string>
	)[props.type];
});

const linkText = computed(() => {
	if (props.moduleName === 'MAIN_JWTV_AVOD') return null;

	return (
		{
			TOP_10: null,
			UPCOMING: tc(`WEBAPP_GLOBAL_UPCOMING_${props.objectType}_SEE_ALL`),
			NEW: tc(`WEBAPP_GLOBAL_NEW_${props.objectType}_SEE_ALL`),
			POPULAR: tc(`WEBAPP_GLOBAL_POPULAR_${props.objectType}_SEE_ALL`),
		} as Record<TitleType, string | null>
	)[props.type];
});

const link = computed(() => {
	if (props.objectType === ObjectType.Movie) {
		if (props.type === 'TOP_10') return null;
		if (props.type === 'POPULAR') return 'app.titles.popular.movie';
		if (props.type === 'NEW') return 'app.titles.new.movie';
		if (props.type === 'UPCOMING') return 'app.titles.upcoming.movie';
	}

	if (props.objectType === ObjectType.Show) {
		if (props.type === 'TOP_10') return null;
		if (props.type === 'POPULAR') return 'app.titles.popular.show';
		if (props.type === 'NEW') return 'app.titles.new.show';
		if (props.type === 'UPCOMING') return 'app.titles.upcoming.show';
	}

	return '';
});

const trackingLabel = computed(() => {
	if (props.objectType === ObjectType.Movie) {
		if (props.type === 'TOP_10') return 'top_10_movies';
		if (props.type === 'POPULAR') return 'popular_movies';
		if (props.type === 'NEW') return 'new_movies';
		if (props.type === 'UPCOMING') return 'upcoming_movies';
	}

	if (props.objectType === ObjectType.Show) {
		if (props.type === 'TOP_10') return 'top_10_shows';
		if (props.type === 'POPULAR') return 'popular_shows';
		if (props.type === 'NEW') return 'new_shows';
		if (props.type === 'UPCOMING') return 'upcoming_shows';
	}

	return '';
});

const observeVisibilityOptions = computed(() => ({
	...defaultObservableOptions,
	callback: (isVisible: boolean) => {
		if (!isVisible || props.isSponsored) return;

		TrackingHelper.trackEvent(ImpressionTrackingEvents.MODULE, { action: 'impression', nonInteraction: true }, [
			moduleContext.value,
		]);
	},
}));

function getTitleContext(title: TitleNode) {
	return new SnowplowTitleContextGraphql(title.objectId, title.objectType);
}

function preventNavigation($event: Event) {
	$event.preventDefault();
	$event.stopPropagation();
	$event.stopImmediatePropagation();
}

function handlePosterClick(title: TitleNode, index: number) {
	emits('posterClick', { title, index });

	if (props.isSponsored) return;

	const selectedTab = getSelectedTab(activeRoute.value?.name as string);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: trackingLabel.value,
			property: selectedTab,
			value: index,
		},
		[getTitleContext(title), moduleContext.value]
	);
}

function handleSeeAllClick() {
	if (props.isSponsored) return;

	TrackingHelper.trackEvent('userinteraction', { action: 'content_click', label: trackingLabel.value }, [
		moduleContext.value,
	]);
}

function handleMobileSlideChange(payload: { direction: string; position: number }) {
	if (props.isSponsored) return;

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'swipe_clicked',
			label: payload.direction,
			value: payload.position,
			property: 'global_home_swiper',
		},
		[moduleContext.value]
	);
}

return { __sfc: true,props, emits, t, tc, isMounted, activeRoute, isLoggedIn, genresByShortName, posterUrls, moduleContext, genreFullName, title, text, linkText, link, trackingLabel, observeVisibilityOptions, getTitleContext, preventNavigation, handlePosterClick, handleSeeAllClick, handleMobileSlideChange, HiddenHorizontalScrollbar, PosterGraphql, TitleDetailLink, Top10TitleSwiper, GlobalTop10SwiperSlide, WatchNowButtonNew }
}

})